import { useCallback, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { refusedHereMessage } from '../../lib/write-refusals';
import { Notice } from './Badge';
import { iconButton } from './Button';
import { IconX } from '../icons';

/**
 * A change this device would not send, said where every screen can show it.
 *
 * Some refusals happen on the device, before any request: the disk is full, the same change
 * is already waiting, or (after an offline start) an office change needs a connection. Many
 * call sites never render their mutation's `.error` (a tech's note, a checklist tick, the
 * timer), so without this the tap simply did nothing. Both shells render it once, under
 * their own chrome; the latest refusal shows until it is dismissed or a newer one replaces
 * it. The server's own refusals stay with the screens that made them.
 */
export function WriteRefusalNotice() {
  const latest = useLatestRefusalHere();
  const [dismissed, setDismissed] = useState<string | null>(null);
  if (!latest || latest === dismissed) return null;
  const message = latest.slice(latest.indexOf(':') + 1);
  return (
    <div className="px-4 pt-3 md:px-6">
      <Notice tone="danger" alert>
        <div className="flex items-start justify-between gap-2">
          <p className="py-2">{message}</p>
          <button
            type="button"
            className={iconButton}
            aria-label="Dismiss"
            onClick={() => setDismissed(latest)}
          >
            <IconX size={16} />
          </button>
        </div>
      </Notice>
    </div>
  );
}

/**
 * The newest mutation in the cache refused on this device, as `mutationId:message` (a
 * string, so the snapshot compares by value), or null.
 */
function useLatestRefusalHere(): string | null {
  const cache = useQueryClient().getMutationCache();
  const subscribe = useCallback(
    (notify: () => void) => cache.subscribe(() => notify()),
    [cache],
  );
  const read = () => {
    let newest: { id: number; message: string } | null = null;
    for (const m of cache.getAll()) {
      if (m.state.status !== 'error') continue;
      const message = refusedHereMessage(m.state.error);
      if (message && (!newest || m.mutationId > newest.id)) {
        newest = { id: m.mutationId, message };
      }
    }
    return newest ? `${newest.id}:${newest.message}` : null;
  };
  return useSyncExternalStore(subscribe, read, read);
}
